import { ExternalLink, FileWarning } from 'lucide-react'
import mammoth from 'mammoth'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchPlaygroundFile } from '@/services/api/playground'
import { parseCsvPreview } from '@/utils/csv-preview'
import { MarkdownPreview } from './MarkdownPreview'

export interface DemoFile {
  filename: string
  fileType: string | null
  fileSize: number
}

const TEXT_LIMIT = 50_000
const CSV_ROWS = 30
/** Scroll-box height of a preview body: bounded by the screen on a phone. */
const PREVIEW_MAX_H = 'max-h-[60dvh] md:max-h-[34rem]'

type PreviewData =
  | { kind: 'pdf'; url: string }
  | { kind: 'docx'; html: string }
  | { kind: 'csv'; rows: string[][]; truncated: boolean }
  | { kind: 'markdown'; text: string }
  | { kind: 'text'; text: string; truncated: boolean }
  | { kind: 'unsupported'; url: string }

/** Fetch the active demo blob and parse it into a renderable preview shape.
 *  Object URLs (`pdf`/`unsupported`) are owned by the caller for revocation. */
async function loadPreview(ext: string): Promise<PreviewData> {
  const blob = await fetchPlaygroundFile()
  if (ext === 'pdf') {
    const pdf =
      blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
    return { kind: 'pdf', url: URL.createObjectURL(pdf) }
  }
  if (ext === 'docx') {
    const { value } = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() })
    return { kind: 'docx', html: value }
  }
  if (ext === 'csv') {
    const rows = parseCsvPreview(await blob.text(), CSV_ROWS + 1)
    return { kind: 'csv', rows: rows.slice(0, CSV_ROWS), truncated: rows.length > CSV_ROWS }
  }
  if (ext === 'md') return { kind: 'markdown', text: await blob.text() }
  if (ext === 'txt' || ext === 'log') {
    const text = await blob.text()
    return { kind: 'text', text: text.slice(0, TEXT_LIMIT), truncated: text.length > TEXT_LIMIT }
  }
  return { kind: 'unsupported', url: URL.createObjectURL(blob) }
}

function usePlaygroundFilePreview(file: DemoFile) {
  const [data, setData] = useState<PreviewData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const { filename, fileType, fileSize } = file

  useEffect(() => {
    let owned: string | undefined
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    loadPreview((fileType || filename.split('.').pop() || '').toLowerCase())
      .then(result => {
        if (cancelled) {
          if ('url' in result) URL.revokeObjectURL(result.url)
          return
        }
        if ('url' in result) owned = result.url
        setData(result)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this file.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      if (owned) URL.revokeObjectURL(owned)
    }
  }, [filename, fileType, fileSize])

  return { data, error, loading }
}

/** Renders the in-page preview body for the active demo file. Meant to sit
 *  inside the upload card, which already shows the filename/size — so this owns
 *  no header of its own. */
export function DemoDocumentPreview({ file }: { file: DemoFile }) {
  const { data, error, loading } = usePlaygroundFilePreview(file)
  return <PreviewBody data={data} error={error} loading={loading} filename={file.filename} />
}

function PreviewBody({
  data,
  error,
  loading,
  filename,
}: {
  data: PreviewData | null
  error: string | null
  loading: boolean
  filename: string
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-[28rem] w-full" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="m-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        {error}
      </div>
    )
  }
  if (!data) return null

  switch (data.kind) {
    case 'pdf':
      return (
        <iframe
          src={data.url}
          title={filename}
          className="h-[60dvh] w-full bg-muted/20 md:h-[34rem]"
        />
      )
    case 'docx':
      return (
        <div
          className={`${PREVIEW_MAX_H} space-y-2 overflow-y-auto px-4 py-3 text-sm leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:font-medium [&_li]:my-0.5 [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_ul]:list-disc [&_ul]:pl-5`}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: mammoth-sanitized docx HTML
          dangerouslySetInnerHTML={{ __html: data.html }}
        />
      )
    case 'csv':
      return <CsvTable rows={data.rows} truncated={data.truncated} />
    case 'markdown':
      return (
        <div className={`${PREVIEW_MAX_H} overflow-y-auto`}>
          <MarkdownPreview content={data.text} />
        </div>
      )
    case 'text':
      return (
        <div className={`${PREVIEW_MAX_H} overflow-y-auto`}>
          <pre className="whitespace-pre-wrap break-words px-4 py-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
            {data.text}
          </pre>
          {data.truncated && (
            <TruncationNote label="Preview truncated to the first 50,000 characters" />
          )}
        </div>
      )
    default:
      return <UnsupportedNote url={data.url} />
  }
}

function CsvTable({ rows, truncated }: { rows: string[][]; truncated: boolean }) {
  return (
    <div className={`${PREVIEW_MAX_H} overflow-auto`}>
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static parsed rows, order is stable
            <tr key={i} className={i === 0 ? 'bg-muted/50 font-medium' : ''}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="border-b border-border px-3 py-1.5 text-foreground whitespace-pre-line align-top"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && <TruncationNote label={`Showing the first ${CSV_ROWS} rows`} />}
    </div>
  )
}

function TruncationNote({ label }: { label: string }) {
  return (
    <div className="border-t border-border/60 py-1.5 text-center text-[11px] italic text-muted-foreground">
      {label}
    </div>
  )
}

function UnsupportedNote({ url }: { url: string }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <FileWarning className="h-7 w-7 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        Inline preview isn’t available for this file type, but it’s still processed below.
      </p>
      <Button asChild variant="outline" size="sm">
        <a href={url} target="_blank" rel="noreferrer">
          <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
          Open file
        </a>
      </Button>
    </div>
  )
}
